// packages/web-ui/src/components/kb-change-tree/ReviewChip.tsx
//
// Story W4-S4 AC #2 — single review chip cycling
// Pending → Accept → Reject → Pending on click. Long-press 500 ms or
// right-click opens the context menu (rendered by the parent via
// `onContextMenu`). UX §10.3 / §10.4 / §10.6.
//
// The chip is stateless w.r.t. the entity status — the parent controls
// the displayed status and the cycle is driven by the `onCycle` callback.

import React, { useEffect, useRef, useState } from 'react';
import type { KbEntityKind, KbEntityStatus } from '@tessa-lean/base';
import { nextStatusInCycle } from './kb-change-tree-helpers.js';

const LONG_PRESS_MS = 500;
const BLOCKED_TOOLTIP_MS = 3000;

export interface ReviewChipProps {
  entityId: string;
  entityKind: KbEntityKind;
  status: KbEntityStatus;
  blocked?: boolean;
  blockedReason?: string;
  onCycle: (next: KbEntityStatus) => void;
  onContextMenu: (anchor: { x: number; y: number }) => void;
  /** When true, disables CSS transitions (NFR-A11Y-5). */
  reducedMotion?: boolean;
}

interface ChipVisual {
  glyph: string;
  text: string;
  className: string;
}

function visualFor(status: KbEntityStatus, blocked: boolean): ChipVisual {
  if (blocked) {
    return { glyph: '⊘', text: 'blocked', className: 'state-blocked' };
  }
  switch (status) {
    case 'pending':
      return { glyph: '◐', text: 'pending', className: 'state-pending' };
    case 'accepted':
      return { glyph: '✓', text: 'accept', className: 'state-accepted' };
    case 'rejected':
      return { glyph: '✗', text: 'reject', className: 'state-rejected' };
    case 'applied':
      return { glyph: '✓', text: 'applied', className: 'state-applied' };
    case 'failed':
      return { glyph: '✗', text: 'failed', className: 'state-failed' };
    case 'unresolvable':
      return { glyph: '⚠', text: 'unresolvable', className: 'state-unresolvable' };
    default:
      return { glyph: '◐', text: 'pending', className: 'state-pending' };
  }
}

// Audi-adapted chip palette — token-driven. Pending = neutral grey
// (no warm cream tint), Reject/Failed = Audi-red family, Accept/Applied
// = brand-balanced cool green, Unresolvable = ochre.
const VARIANT_STYLE: Record<string, React.CSSProperties> = {
  'state-pending': {
    color: 'var(--color-taupe-700)',
    borderColor: 'var(--color-taupe-500)',
    background: 'var(--color-cream-100)',
  },
  'state-accepted': {
    color: 'var(--color-accent-green)',
    borderColor: 'var(--color-accent-green)',
    background: 'var(--color-accent-green-bg)',
  },
  'state-rejected': {
    color: 'var(--color-audi-red)',
    borderColor: 'var(--color-audi-red)',
    background: 'var(--color-accent-red-bg)',
  },
  'state-applied': {
    color: 'var(--color-accent-green)',
    borderColor: 'var(--color-accent-green)',
    background: 'var(--color-accent-green-bg)',
    fontWeight: 600,
  },
  'state-failed': {
    color: 'var(--color-audi-red)',
    borderColor: 'var(--color-audi-red)',
    background: 'var(--color-accent-red-bg)',
    fontWeight: 700,
  },
  'state-unresolvable': {
    color: 'var(--color-accent-amber)',
    borderColor: 'var(--color-accent-amber)',
    background: 'var(--color-accent-amber-bg)',
  },
  'state-blocked': {
    color: 'var(--color-taupe-700)',
    borderColor: 'var(--color-taupe-500)',
    background: 'var(--color-cream-100)',
    borderStyle: 'dashed',
  },
};

export function ReviewChip({
  entityId,
  entityKind: _entityKind,
  status,
  blocked = false,
  blockedReason,
  onCycle,
  onContextMenu,
  reducedMotion = false,
}: ReviewChipProps): React.ReactElement {
  const longPressTimerRef = useRef<number | null>(null);
  const longPressFiredRef = useRef(false);
  const blockedTooltipTimerRef = useRef<number | null>(null);
  const [showBlockedTooltip, setShowBlockedTooltip] = useState(false);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (longPressTimerRef.current !== null) {
        window.clearTimeout(longPressTimerRef.current);
      }
      if (blockedTooltipTimerRef.current !== null) {
        window.clearTimeout(blockedTooltipTimerRef.current);
      }
    };
  }, []);

  const visual = visualFor(status, blocked);

  const cycle = (): void => {
    if (blocked) {
      // Show tooltip for 3 s
      setShowBlockedTooltip(true);
      if (blockedTooltipTimerRef.current !== null) {
        window.clearTimeout(blockedTooltipTimerRef.current);
      }
      blockedTooltipTimerRef.current = window.setTimeout(() => {
        setShowBlockedTooltip(false);
        blockedTooltipTimerRef.current = null;
      }, BLOCKED_TOOLTIP_MS);
      return;
    }
    const next = nextStatusInCycle(status);
    if (next !== null) onCycle(next);
  };

  const handleClick = (): void => {
    if (longPressFiredRef.current) {
      // Click is the synthetic up-event after a long-press; suppress
      longPressFiredRef.current = false;
      return;
    }
    cycle();
  };

  const handlePointerDown = (ev: React.PointerEvent<HTMLButtonElement>): void => {
    // Only respond to primary (or unspecified) button — `fireEvent.pointerDown`
    // in test setups may leave `button` undefined.
    if (ev.button !== undefined && ev.button !== 0) return;
    longPressFiredRef.current = false;
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
    }
    const x = typeof ev.clientX === 'number' ? ev.clientX : 0;
    const y = typeof ev.clientY === 'number' ? ev.clientY : 0;
    longPressTimerRef.current = window.setTimeout(() => {
      longPressFiredRef.current = true;
      longPressTimerRef.current = null;
      onContextMenu({ x, y });
    }, LONG_PRESS_MS);
  };

  const cancelLongPress = (): void => {
    if (longPressTimerRef.current !== null) {
      window.clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleContextMenu = (ev: React.MouseEvent<HTMLButtonElement>): void => {
    ev.preventDefault();
    cancelLongPress();
    onContextMenu({ x: ev.clientX, y: ev.clientY });
  };

  const ariaLabel = blocked
    ? `Review state for ${entityId}: blocked. ${blockedReason ?? ''}`.trim()
    : `Review state for ${entityId}: ${status}. Click to cycle.`;

  const transitionStyle: React.CSSProperties = reducedMotion
    ? { transition: 'none' }
    : { transition: 'background-color 100ms, border-color 100ms' };

  const style: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.125rem 0.5rem',
    borderRadius: '0px',
    border: '1px solid',
    fontSize: '0.75rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    cursor: blocked ? 'not-allowed' : 'pointer',
    ...VARIANT_STYLE[visual.className],
    ...transitionStyle,
  };

  return (
    <span style={{ position: 'relative', display: 'inline-block' }}>
      <button
        type="button"
        data-testid={`review-chip-${entityId}`}
        data-status={status}
        data-blocked={blocked ? 'true' : 'false'}
        className={`review-chip ${visual.className}${reducedMotion ? ' motion-none' : ''}`}
        style={style}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-disabled={blocked}
        tabIndex={0}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onPointerDown={handlePointerDown}
        onPointerUp={cancelLongPress}
        onPointerCancel={cancelLongPress}
        onPointerLeave={cancelLongPress}
      >
        <span aria-hidden="true">{visual.glyph}</span>
        <span>{visual.text}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {showBlockedTooltip && blockedReason !== undefined && (
        <div
          role="tooltip"
          data-testid={`review-chip-blocked-tooltip-${entityId}`}
          style={{
            position: 'absolute',
            bottom: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            marginBottom: '0.25rem',
            padding: '0.25rem 0.5rem',
            background: 'var(--color-taupe-900)',
            color: '#FFFFFF',
            borderRadius: '2px',
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            zIndex: 10,
          }}
        >
          {blockedReason}
        </div>
      )}
    </span>
  );
}
